<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSettingsMenu } from 'dashboard/components-next/sidebar/useSettingsMenu';
import { useNavAccess } from 'dashboard/components-next/sidebar/provider';
import PageHeader from 'dashboard/components-next/polaris/cards/PageHeader.vue';
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from 'dashboard/components-next/polaris/ui/item';
import SettingsLayout from '../SettingsLayout.vue';

// The Settings Home (D-S1, R2-9-9; the kit's SettingsHome): every settings page, grouped as in the menu, each
// explained in one sentence. It reads the menu itself, so permissions and flags hide a card with its item.
const { t, te } = useI18n();
const { settingsItems } = useSettingsMenu();
const { isAllowed } = useNavAccess();

// `settings-agents` → SETTINGS_HOME.SENTENCES.AGENTS
const sentenceOf = item => {
  const key = `SETTINGS_HOME.SENTENCES.${item.name.replace('settings-', '').replace(/-/g, '_').toUpperCase()}`;
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return te(key) ? t(key) : '';
};

const groups = computed(() =>
  settingsItems.value
    .filter(group => group.children)
    .map(group => ({
      ...group,
      children: group.children
        .filter(item => isAllowed(item.to))
        .map(item => ({
          ...item,
          sentence: item.sentence || sentenceOf(item),
        })),
    }))
    .filter(group => group.children.length)
);
</script>

<template>
  <SettingsLayout>
    <template #header>
      <PageHeader :title="t('SETTINGS_HOME.TITLE')" />
    </template>
    <template #body>
      <div class="@container grid gap-8">
        <section
          v-for="group in groups"
          :key="group.name"
          :aria-label="group.label"
          class="grid gap-3"
        >
          <h2 class="type-polaris-card-title">{{ group.label }}</h2>
          <ul
            role="list"
            class="m-0 grid list-none gap-3 p-0 @xl:grid-cols-2 @3xl:grid-cols-3 @6xl:grid-cols-4"
          >
            <li v-for="item in group.children" :key="item.name" class="min-w-0">
              <Item
                as-child
                variant="outline"
                size="sm"
                class="h-full w-full flex-nowrap items-start text-start hover:bg-polaris-accent"
              >
                <router-link :to="item.to">
                  <ItemMedia variant="icon">
                    <component :is="item.icon" aria-hidden="true" />
                  </ItemMedia>
                  <ItemContent class="min-w-0 gap-0.5">
                    <ItemTitle>{{ item.label }}</ItemTitle>
                    <ItemDescription
                      v-if="item.sentence"
                      class="line-clamp-none text-wrap type-polaris-caption"
                    >
                      {{ item.sentence }}
                    </ItemDescription>
                  </ItemContent>
                </router-link>
              </Item>
            </li>
          </ul>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
